import { ContextStore } from 'bernie-plugin-mobx';

import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { URL_PARAM_TO_QUERY_ID_MAPPER } from 'stores/models';

import { MarketingChannels } from 'constants/marketing-channels';
import { MarketingPrices } from 'constants/marketing-prices';

import {
  adaptCountsFromStrings,
  adaptRangesFromStrings,
  adaptSelectionFromEnum,
  adaptSelectionFromString,
  adaptSelectionsFromEnums,
  adaptSelectionsFromStrings,
} from './adapter-utils';

import {
  AgencyBusinessModel,
  CleaningAndSafetyPractice,
  CommissionTier,
  DealType,
  Listings,
  MealPlanFilterAmenity,
  NumberValueInput,
  PaginationInput,
  PaymentType,
  Points,
  PrivacyTrackingState,
  PropertyFilterAccessibility,
  PropertyFilterAmenity,
  PropertySearchCriteriaInput,
  PropertySort,
  PropertyStyle,
  SelectedValueInput,
  StayType,
  StructureType,
  TravelerType,
} from '__generated__/api';
import { getDateRangeInput, getDestinationInput, getRooms, getSearchCriteriaSecondaryBooleans } from './search-input-adapter';

export interface ExtendedCriteria extends SearchCriteriaModel {
  authSuccessMsg?: string;
  // eslint-disable-next-line camelcase
  exp_dp?: string;
  // eslint-disable-next-line camelcase
  exp_tx?: string;
  rfrr?: string;
  // eslint-disable-next-line camelcase
  top_cur?: string;
  // eslint-disable-next-line camelcase
  top_dp?: string;
  // eslint-disable-next-line camelcase
  trv_bp?: string;
  // eslint-disable-next-line camelcase
  trv_curr?: string;
  // eslint-disable-next-line camelcase
  trv_dp?: string;
}

export function adaptSearchCriteria(
  context: ContextStore,
  extendedCriteria: ExtendedCriteria,
  adsOnly: boolean,
  searchPagination?: PaginationInput
): PropertySearchCriteriaInput {
  const criteria = { ...extendedCriteria } as ExtendedCriteria;
  Object.keys(criteria).forEach(key => {
    if (criteria[key] === '' || (Array.isArray(criteria[key]) && criteria[key].length === 0)) {
      delete criteria[key];
    }
  });

  criteria.semdtl = sanitizeSemdtl(criteria);

  const searchCriteria = {
    primary: {
      dateRange: getDateRangeInput(criteria),
      destination: getDestinationInput(criteria),
      rooms: getRooms(criteria),
    },
    secondary: {
      counts: [
        ...adaptCountsFromStrings('bedroomFilter', 'bedroomFilter', criteria),
        ...adaptPagination(searchPagination),
        ...adaptCountsFromStrings('price', 'price', criteria),
        ...adaptCountsFromStrings('total_price', 'total_price', criteria),
        ...adaptCountsFromStrings('nightly_price', 'nightly_price', criteria),
      ].filter(count => count),
      booleans: getSearchCriteriaSecondaryBooleans(criteria, URL_PARAM_TO_QUERY_ID_MAPPER),
      selections: [
        adaptSelectionFromEnum('sort', 'sort', criteria, PropertySort),
        adaptSelectionFromString('chain', 'chain', criteria),
        adaptSelectionFromString('regionId', 'neighborhood', criteria),
        adaptSelectionFromString('poi', 'poi', criteria),
        adaptSelectionFromString('propertyName', 'hotelName', criteria),
        adaptSelectionFromString('hotelName', 'hotelName', criteria),
        adaptSelectionFromString('semdtl', 'semdtl', criteria),
        adaptSelectionFromString('theme', 'theme', criteria),
        adaptSelectionFromString('paandi', 'paandi', criteria),
        adaptSelectionFromString('userIntent', 'userIntent', criteria),
        adaptSelectionFromString('bedroom_count_gt', 'bedroom_count_gt', criteria),
        adaptSelectionFromString('us_bathroom_count_gt', 'us_bathroom_count_gt', criteria),
        adaptSelectionFromString('guestRating', 'guestRating', criteria),
        adaptTrackingState(context),
        ...adaptSelectionsFromEnums('accessibility', 'accessibility', criteria, PropertyFilterAccessibility),
        ...adaptSelectionsFromEnums('agencyBusinessModels', 'agencyBusinessModel', criteria, AgencyBusinessModel),
        ...adaptSelectionsFromEnums('amenities', 'amenities', criteria, PropertyFilterAmenity),
        ...adaptSelectionsFromStrings('bedroomFilter', 'bedroomFilter', criteria).filter(selection => selection),
        ...adaptSelectionsFromEnums('commissionTiers', 'commissionTier', criteria, CommissionTier),
        ...adaptSelectionsFromEnums('deals', 'deals', criteria, DealType),
        ...adaptSelectionsFromEnums('mealPlan', 'mealPlan', criteria, MealPlanFilterAmenity),
        ...adaptSelectionsFromEnums('paymentType', 'paymentType', criteria, PaymentType),
        ...adaptSelectionsFromEnums('propertyStyles', 'propertyStyles', criteria, PropertyStyle),
        ...adaptSelectionsFromStrings('bedType', 'bedType', criteria).filter(selection => selection),
        ...adaptSelectionsFromEnums('cleaningAndSafetyPractices', 'cleaningAndSafetyPractices', criteria, CleaningAndSafetyPractice),
        ...adaptSelectionsFromStrings('rewards', 'rewards', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('starRating', 'star', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('location_group', 'location_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('property_type_group', 'property_type_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('accessibility_features_group', 'accessibility_features_group', criteria).filter(
          selection => selection
        ),
        ...adaptSelectionsFromStrings('booking_options_group', 'booking_options_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('discounts_group', 'discounts_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('amenities_facilities_group', 'amenities_facilities_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('free_cancellation_group', 'free_cancellation_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('house_rules_group', 'house_rules_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('nearby_activities_group', 'nearby_activities_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('neighborhood_group', 'neighborhood_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('premier_host_group', 'premier_host_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('properties_good_for_group', 'properties_good_for_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('property_reviews_group', 'property_reviews_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('rooms_spaces_group', 'rooms_spaces_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('safety_group', 'safety_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('virtual_tour_group', 'virtual_tour_group', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('star', 'star', criteria).filter(selection => selection),
        ...adaptSelectionsFromEnums('structureTypes', 'lodging', criteria, StructureType),
        ...adaptSelectionsFromEnums('stayType', 'stayType', criteria, StayType),
        ...adaptSelectionsFromEnums('lodging', 'lodging', criteria, StructureType),
        ...adaptSelectionsFromEnums('travelerType', 'travelerType', criteria, TravelerType),
        ...adaptMarketingInfo(criteria).filter(selection => selection),
        ...adaptPropertyShopOptions(criteria.useRewards, adsOnly),
      ].filter(selection => selection),
      ranges: [...adaptRangesFromStrings('price', 'price', criteria)].filter(range => range),
    },
  };

  return searchCriteria;
}

function sanitizeSemdtl(criteria: ExtendedCriteria): string {
  const semdtl = criteria.semdtl;

  // semdtl is probably an observable array if not a string
  return semdtl && typeof semdtl !== 'string' && semdtl.length > 0 ? semdtl.filter(p => p)[0] : semdtl;
}

function adaptMarketingInfo(criteria: ExtendedCriteria): SelectedValueInput[] {
  return Object.keys(criteria).map(id => {
    const value = criteria[id];

    if (value && (id.toLowerCase() in MarketingChannels || id.toLowerCase() in MarketingPrices)) {
      return { id, value };
    }

    return undefined;
  });
}

function adaptPropertyShopOptions(useRewards: string, adsOnly: boolean): SelectedValueInput[] {
  const selections: SelectedValueInput[] = [];

  let points;

  if (useRewards === 'true') {
    points = Points.SHOP_WITH_POINTS;
  } else if (useRewards === 'false') {
    points = Points.SHOP_WITHOUT_POINTS;
  }

  if (points) {
    selections.push({ id: 'useRewards', value: points });
  }

  if (adsOnly) {
    selections.push({ id: 'listings', value: Listings.ADS_ONLY });
  }

  return selections;
}

function adaptPagination(searchPagination?: PaginationInput): NumberValueInput[] {
  if (searchPagination) {
    return [
      { id: 'resultsStartingIndex', value: searchPagination.startingIndex },
      { id: 'resultsSize', value: searchPagination.size },
    ];
  }

  return [];
}

function adaptTrackingState(context: ContextStore): SelectedValueInput {
  const id = 'privacyTrackingState';

  if (
    context.site?.optInPrivacyInfo &&
    (context.site?.optInPrivacyInfo.canTrack || (context.site?.optInPrivacyInfo.set && context.site?.optInPrivacyInfo.yesSelected))
  ) {
    return { id, value: PrivacyTrackingState.CAN_TRACK };
  }

  return { id, value: PrivacyTrackingState.CAN_NOT_TRACK };
}
